type Job = { id: string; title: string | null };
type UploadHeader = { name: string; value: string };
type Upload = { filename: string; uploadUrl: string; headers: UploadHeader[] };
type PrepareResponse = { token?: string; uploads?: Upload[]; message?: string };
type Picked = { file: File; kind: 'resume' | 'supporting' };

const RESUME_MAX = 10 * 1024 * 1024;
const FILE_MAX = 25 * 1024 * 1024;
const EXTRA_MAX_FILES = 5;
// Walu rejects a file unless its content type is exactly the one mapped to its extension.
const CONTENT_TYPES: Record<string, string> = {
	pdf: 'application/pdf',
	doc: 'application/msword',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	png: 'image/png',
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
};
const RESUME_EXT = ['pdf', 'doc', 'docx'];
const EXTRA_EXT = ['pdf', 'png', 'jpg', 'jpeg'];
const FALLBACK_ERROR = 'Unable to submit this application.';

const byId = (id: string) => document.getElementById(id);

function jobs(): Job[] {
	const raw = byId('careers-jobs')?.textContent ?? '[]';
	try {
		const parsed = JSON.parse(raw) as Job[];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

function esc(value: string): string {
	return value.replace(/[&<>"']/g, (ch) => {
		switch (ch) {
			case '&':
				return '&amp;';
			case '<':
				return '&lt;';
			case '>':
				return '&gt;';
			case '"':
				return '&quot;';
			default:
				return '&#39;';
		}
	});
}

function extOf(name: string): string {
	const dot = name.lastIndexOf('.');
	return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

function withHttps(value: string): string {
	const trimmed = value.trim();
	if (!trimmed || /^https?:\/\//i.test(trimmed)) return trimmed;
	return `https://${trimmed}`;
}

function show(which: 'c-list' | 'c-post' | 'c-apply' | 'c-done') {
	(['c-list', 'c-post', 'c-apply', 'c-done'] as const).forEach((id) => {
		const el = byId(id);
		if (el) el.hidden = id !== which;
	});
	window.scrollTo(0, 0);
}

function setFieldError(id: string, message: string) {
	const wrap = byId(`${id}-w`);
	wrap?.classList.toggle('is-err', Boolean(message));
	const err = wrap?.querySelector('.portal-err');
	if (err) {
		err.id = `${id}-err`;
		err.textContent = message;
	}
	const input = byId(id);
	if (!input) return;
	if (message) {
		input.setAttribute('aria-invalid', 'true');
		input.setAttribute('aria-describedby', `${id}-err`);
	} else {
		input.removeAttribute('aria-invalid');
		input.removeAttribute('aria-describedby');
	}
}

function radio(name: string): string {
	const checked = document.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`);
	return checked?.value ?? '';
}

function formatBytes(bytes: number): string {
	if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fitsRules(file: File, extensions: string[], maxBytes: number): boolean {
	return extensions.includes(extOf(file.name)) && file.size > 0 && file.size <= maxBytes;
}

function contentTypeOf(file: File): string {
	return CONTENT_TYPES[extOf(file.name)] ?? (file.type || 'application/octet-stream');
}

function fileMeta(file: File) {
	return {
		filename: file.name,
		contentType: contentTypeOf(file),
		sizeBytes: file.size,
	};
}

class CareersPortal {
	private roleId = '';
	private resume: Picked[] = [];
	private extra: Picked[] = [];
	private working = false;

	constructor(private root: HTMLElement) {
		this.roleId = root.dataset.jobId ?? '';
		this.fillRoles();
		this.bind();
		window.addEventListener('popstate', () => this.route());
		if (new URLSearchParams(window.location.search).has('apply')) this.route();
	}

	// The form lives at ?apply=<jobId|general> so browser back/forward and refresh behave like page loads.
	private route() {
		const apply = new URLSearchParams(window.location.search).get('apply');
		if (apply === null) {
			show(this.roleId ? 'c-post' : 'c-list');
			return;
		}
		this.openApply(apply || this.roleId || 'general', false);
	}

	private baseUrl(): string {
		const url = new URL(window.location.href);
		url.searchParams.delete('apply');
		return url.toString();
	}

	private fillRoles() {
		const select = byId('f-role') as HTMLSelectElement | null;
		if (!select) return;
		const options = jobs()
			.map((job) => `<option value="${esc(job.id)}">${esc(job.title ?? 'Open role')}</option>`)
			.join('');
		select.innerHTML = `${options}<option value="general">General application</option>`;
		select.value = this.roleId || 'general';
		select.addEventListener('change', () => this.setTitle(select.value));
		this.setTitle(select.value);
	}

	private setTitle(id: string) {
		const title = byId('a-title');
		if (!title) return;
		if (id === 'general') {
			title.textContent = 'General application';
			return;
		}
		title.textContent = jobs().find((job) => job.id === id)?.title ?? 'Apply';
	}

	private bind() {
		this.root.querySelectorAll<HTMLButtonElement>('[data-apply]').forEach((button) => {
			button.addEventListener('click', () => this.openApply(button.dataset.apply ?? 'general'));
		});
		byId('p-apply')?.addEventListener('click', () => this.openApply(this.roleId || 'general'));
		byId('c-back')?.addEventListener('click', () => {
			if ((history.state as { portal?: boolean } | null)?.portal) {
				history.back();
				return;
			}
			history.replaceState(null, '', this.baseUrl());
			this.route();
		});
		this.bindDrop('resume', 'drop-resume', 'f-resume');
		this.bindDrop('supporting', 'drop-extra', 'f-extra');
		byId('a-form')?.addEventListener('submit', (event) => {
			event.preventDefault();
			void this.submit();
		});
	}

	private openApply(requested: string, push = true) {
		const select = byId('f-role') as HTMLSelectElement | null;
		const id = jobs().some((job) => job.id === requested) ? requested : 'general';
		if (select) select.value = id;
		this.setTitle(id);
		if (push) {
			const url = new URL(window.location.href);
			url.searchParams.set('apply', id);
			history.pushState({ portal: true }, '', url.toString());
		}
		const back = byId('c-back');
		if (back) back.textContent = this.roleId ? '← Back to posting' : '← All open positions';
		show('c-apply');
	}

	private bindDrop(kind: Picked['kind'], zoneId: string, inputId: string) {
		const zone = byId(zoneId);
		const input = byId(inputId) as HTMLInputElement | null;
		if (!zone || !input) return;
		input.addEventListener('change', () => {
			if (input.files?.length) this.addFiles(kind, input.files);
			input.value = '';
		});
		zone.addEventListener('dragover', (event) => {
			event.preventDefault();
			zone.classList.add('is-over');
		});
		zone.addEventListener('dragleave', (event) => {
			const next = event.relatedTarget;
			if (next instanceof Node && zone.contains(next)) return;
			zone.classList.remove('is-over');
		});
		zone.addEventListener('drop', (event) => {
			event.preventDefault();
			zone.classList.remove('is-over');
			const files = event.dataTransfer?.files;
			if (files?.length) this.addFiles(kind, files);
		});
	}

	private addFiles(kind: Picked['kind'], list: FileList) {
		const incoming = [...list];
		if (kind === 'resume') {
			const file = incoming[0];
			if (!file) return;
			const ok = fitsRules(file, RESUME_EXT, RESUME_MAX);
			if (ok) this.resume = [{ file, kind: 'resume' }];
			setFieldError(
				'f-resume',
				ok ? '' : `${file.name} wasn't added. Use a PDF or Word resume, 10 MB or smaller.`
			);
		} else {
			const valid = incoming.filter((file) => fitsRules(file, EXTRA_EXT, FILE_MAX));
			const rejected = incoming.filter((file) => !valid.includes(file));
			const room = EXTRA_MAX_FILES - this.extra.length;
			this.extra = [
				...this.extra,
				...valid.slice(0, room).map((file) => ({ file, kind: 'supporting' as const })),
			];
			const messages: string[] = [];
			if (rejected.length) {
				const what =
					rejected.length === 1 ? `${rejected[0].name} wasn't` : `${rejected.length} files weren't`;
				messages.push(`${what} added. Use PDF or image files, 25 MB or smaller.`);
			}
			if (valid.length > room) {
				messages.push(`You can attach up to ${EXTRA_MAX_FILES} supporting files.`);
			}
			setFieldError('f-extra', messages.join(' '));
		}
		this.renderFiles(kind);
	}

	private removeFile(kind: Picked['kind'], index: number) {
		if (kind === 'resume') this.resume = [];
		else {
			this.extra = this.extra.filter((_, i) => i !== index);
			setFieldError('f-extra', '');
		}
		this.renderFiles(kind);
	}

	private renderFiles(kind: Picked['kind']) {
		const list = byId(kind === 'resume' ? 'list-resume' : 'list-extra');
		const items = kind === 'resume' ? this.resume : this.extra;
		if (!list) return;
		list.replaceChildren();
		items.forEach((item, index) => {
			const chip = document.createElement('div');
			chip.className = 'portal-chip';
			const name = document.createElement('span');
			name.className = 'n';
			name.textContent = item.file.name;
			const size = document.createElement('span');
			size.className = 's';
			size.textContent = formatBytes(item.file.size);
			const remove = document.createElement('button');
			remove.type = 'button';
			remove.setAttribute('aria-label', `Remove ${item.file.name}`);
			remove.textContent = '×';
			remove.addEventListener('click', () => this.removeFile(kind, index));
			chip.append(name, size, remove);
			list.append(chip);
		});
	}

	private validate(): boolean {
		let ok = true;
		const value = (id: string) => (byId(id) as HTMLInputElement | null)?.value.trim() ?? '';
		const required: Array<[string, string]> = [
			['f-first', 'Enter your first name.'],
			['f-last', 'Enter your last name.'],
			['f-phone', 'Enter a phone number so we can reach you.'],
		];
		required.forEach(([id, message]) => {
			const bad = !value(id);
			setFieldError(id, bad ? message : '');
			if (bad) ok = false;
		});
		const email = value('f-email');
		const badEmail = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
		setFieldError('f-email', badEmail ? 'Enter an email like name@example.com.' : '');
		if (badEmail) ok = false;
		// addFiles only accepts files that fit the rules, so a missing resume is all that's left to check.
		setFieldError('f-resume', this.resume.length ? '' : 'Add your resume to continue.');
		if (!this.resume.length) ok = false;
		const consent = (byId('f-consent') as HTMLInputElement | null)?.checked ?? false;
		const consentErr = byId('err-consent');
		if (consentErr)
			consentErr.textContent = consent ? '' : 'Check the box so we can store your application.';
		if (!consent) ok = false;
		return ok;
	}

	private formError(message: string) {
		const el = byId('err-form');
		if (el) el.textContent = message;
	}

	private async submit() {
		if (this.working) return;
		this.formError('');
		if (!this.validate()) return;
		if (typeof grecaptcha === 'undefined') {
			this.formError('Unable to verify your submission. Please disable ad blockers and try again.');
			return;
		}
		this.working = true;
		const submit = byId('f-submit') as HTMLButtonElement | null;
		const label = submit?.textContent ?? '';
		if (submit) {
			submit.disabled = true;
			submit.setAttribute('aria-busy', 'true');
			submit.textContent = 'Submitting…';
		}
		try {
			const token = await new Promise<string>((resolve, reject) => {
				grecaptcha.ready(() => {
					grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' }).then(resolve).catch(reject);
				});
			});
			const prepared = await this.post('careers_apply_prepare', token, {
				payload: JSON.stringify(this.payload()),
				company_website: (byId('company_website') as HTMLInputElement | null)?.value ?? '',
			});
			if (!prepared.token || !prepared.uploads) {
				throw new Error(prepared.message || FALLBACK_ERROR);
			}
			await this.uploadAll(prepared.uploads);
			const completed = await this.post('careers_apply_complete', '', { token: prepared.token });
			if (completed.message && !('taskId' in completed)) {
				throw new Error(completed.message);
			}
			this.done();
		} catch (err) {
			const message = err instanceof Error ? err.message : FALLBACK_ERROR;
			this.formError(`${message} You can email careers@freeflysystems.com instead.`);
		} finally {
			this.working = false;
			if (submit) {
				submit.disabled = false;
				submit.removeAttribute('aria-busy');
				submit.textContent = label;
			}
		}
	}

	private payload() {
		const value = (id: string) =>
			(byId(id) as HTMLInputElement | HTMLTextAreaElement | null)?.value.trim() ?? '';
		const select = byId('f-role') as HTMLSelectElement | null;
		return {
			jobId: select?.value === 'general' ? null : select?.value,
			firstName: value('f-first'),
			lastName: value('f-last'),
			email: value('f-email'),
			phone: value('f-phone'),
			linkedin: withHttps(value('f-linkedin')),
			portfolio: withHttps(value('f-portfolio')),
			github: withHttps(value('f-github')),
			city: value('f-city'),
			onsite: radio('onsite'),
			visa: radio('visa'),
			pay: value('f-comp'),
			note: value('f-note'),
			consent: true,
			resume: fileMeta(this.resume[0].file),
			files: this.extra.map((item) => fileMeta(item.file)),
		};
	}

	private async post(
		action: string,
		recaptcha: string,
		fields: Record<string, string>
	): Promise<PrepareResponse & { taskId?: string }> {
		const body = new FormData();
		body.append('action', action);
		if (recaptcha) body.append('g-recaptcha-response', recaptcha);
		Object.entries(fields).forEach(([key, field]) => body.append(key, field));
		const response = await fetch(ajaxurl, { method: 'POST', body });
		let data: PrepareResponse & { taskId?: string };
		try {
			data = (await response.json()) as PrepareResponse & { taskId?: string };
		} catch {
			// admin-ajax, a PHP fatal, or a gateway timeout can answer with HTML or a bare "0".
			throw new Error(FALLBACK_ERROR);
		}
		if (!response.ok) throw new Error(data.message || FALLBACK_ERROR);
		return data;
	}

	private async uploadAll(uploads: Upload[]) {
		// Walu returns uploads in submission order (resume first) and renames the resume, so match by index.
		const files = [...this.resume, ...this.extra];
		if (uploads.length !== files.length) throw new Error('Unable to upload your files.');
		await Promise.all(
			uploads.map(async (upload, index) => {
				const headers = new Headers();
				upload.headers.forEach((header) => {
					if (header.name.toLowerCase() === 'content-length') return;
					headers.set(header.name, header.value);
				});
				const response = await fetch(upload.uploadUrl, {
					method: 'PUT',
					body: files[index].file,
					headers,
				});
				if (!response.ok) throw new Error(`Could not upload ${upload.filename}.`);
			})
		);
	}

	private done() {
		const select = byId('f-role') as HTMLSelectElement | null;
		const role =
			select?.value === 'general'
				? 'a general position'
				: (jobs().find((job) => job.id === select?.value)?.title ?? 'this role');
		const heading = byId('d-h');
		const copy = byId('d-p');
		const first = (byId('f-first') as HTMLInputElement | null)?.value.trim() ?? '';
		const email = (byId('f-email') as HTMLInputElement | null)?.value.trim() ?? '';
		if (heading) heading.textContent = first ? `Thanks, ${first}!` : 'Application received';
		if (copy) {
			copy.textContent = `We received your application for ${role}. Our team reviews every application, and we'll reach out at ${email} if it's a match.`;
		}
		// Drop ?apply so back/refresh never reopens a submitted form.
		history.replaceState(null, '', this.baseUrl());
		show('c-done');
	}
}

const root = document.getElementById('careers-portal');
if (root) new CareersPortal(root);
